import React from 'react';
import { Clock, Users, TrendingUp } from 'lucide-react';
import { type HeroContent } from '../lib/variants';

interface HeroProps {
  onOpenBooking: () => void;
  content: HeroContent;
}

const icons = [
  <Clock className="h-4 w-4" key="clock" />,
  <Users className="h-4 w-4" key="users" />,
  <TrendingUp className="h-4 w-4" key="trending" />,
];

const Hero: React.FC<HeroProps> = ({ onOpenBooking, content }) => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 pt-28 pb-20 lg:pt-36 lg:pb-28 text-white">
      <div className="absolute top-16 left-1/2 h-72 w-[48rem] -translate-x-1/2 rounded-full bg-cyan-400/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-400/60 bg-orange-500/10 px-4 py-2 text-sm font-bold text-orange-300">
            <span className="inline-block h-2 w-2 rounded-full bg-orange-400 animate-pulse" />
            {content.urgencyBadge}
          </div>
        </div>

        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl lg:text-6xl font-extrabold text-slate-50 tracking-tight leading-tight mb-5">
            {content.headline}
            <br />
            <span className="text-cyan-400">{content.headlineAccent}</span>
          </h1>

          <p className="text-xl lg:text-2xl font-semibold text-slate-300 mb-4 leading-relaxed">
            {content.subheadline}
          </p>

          <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            {content.description}
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {content.painPoints.map((item, i) => (
              <div key={i} className="flex items-center gap-2 rounded-full bg-slate-800/60 border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200">
                <span className="text-cyan-400">{icons[i] || icons[0]}</span>
                {item.text}
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={onOpenBooking}
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-cyan-400 hover:bg-cyan-300 text-slate-950 rounded-xl font-extrabold text-lg shadow-2xl shadow-cyan-500/30 transition transform hover:-translate-y-0.5"
            >
              {content.ctaPrimary}
            </button>
            <a
              href="#jak-to-dziala"
              className="inline-flex items-center justify-center gap-2 px-8 py-5 bg-slate-800/60 border-2 border-cyan-400/30 text-cyan-100 hover:bg-slate-800 rounded-xl font-bold text-lg transition"
            >
              {content.ctaSecondary}
            </a>
          </div>

          <p className="text-sm text-slate-500">
            {content.trustSignals}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-3 gap-6 max-w-2xl mx-auto text-center">
          {content.stats.map((stat, i) => (
            <div key={i}>
              <div className="text-3xl font-extrabold text-cyan-400">{stat.value}</div>
              <div className="text-xs text-slate-400 mt-1 leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
